import api from "@api";
import { components, operations, paths } from "@api/schema";
import { IOrder } from "./orders";

export type IBasket = components["schemas"]["basket"];

export type IBasketItem = IBasket["items"][number];

export type IUnsavedBasketItem = Pick<
  IBasketItem,
  "itemId" | "itemType" | "quantity"
>;

export type IUnsavedBasket =
  | Omit<IBasket, "items"> & { items: IUnsavedBasketItem[] };

export const saveBasket = async <T extends IBasket | IUnsavedBasket>(
  basket: T
): Promise<IBasket> => {
  try {
    const { data } = await api.post<T, IBasket>("baskets", basket);

    return data;
  } catch (error) {
    throw error;
  }
};

export const loadBasket = async (id: IBasket["id"]): Promise<IBasket> => {
  try {
    const { data } = await api.get<undefined, IBasket>(`baskets/${id}`);

    return data;
  } catch (error) {
    throw error;
  }
};

type ILoadBasketsQuery =
  paths["/baskets"]["get"]["parameters"]["query"]["query"];

export const loadBasketByItem = async (
  item?: IUnsavedBasketItem
): Promise<IBasket> => {
  try {
    const { data } = await api.get<ILoadBasketsQuery, IBasket[]>(
      `baskets?q[items][][item_id]=${item?.itemId}&q[items][][item_type]=${item?.itemType}`
    );

    const basket = data[data.length - 1];

    if (!basket) {
      throw new Error("Basket not found");
    }

    return basket;
  } catch (error) {
    throw error;
  }
};

export type IOrderPreviewBody =
  operations["previewBasketOrder"]["requestBody"]["content"]["application/json"];

export const previewBasketOrder = async (
  basketId: IBasket["id"],
  basketParams: IOrderPreviewBody
): Promise<IOrder> => {
  try {
    const { data } = await api.post<IOrderPreviewBody, IOrder>(
      `baskets/${basketId}/preview_order`,
      basketParams
    );

    return data;
  } catch (error) {
    throw error;
  }
};
